/**
 * Every partner endpoint the panel calls, in one place (apps/api README, «Partner sign-in»,
 * «Routes», «Portal API»). Response types live in `@mfa-aff/shared` (packages/shared/src/api/).
 * Swapping a hook to real data = VITE_USE_MOCKS=false, nothing else.
 */
const P = "/api/v1/partner";

export const partnerPaths = {
    /** Public: is the panel open (owner switch `features.partner_panel_open`). */
    status: `${P}/status`,

    // session and sign-in (design doc §13)
    /** Browser navigation (302 to WHMCS), not a fetch. */
    oidcStart: `${P}/auth/oidc/start`,
    oidcChoose: `${P}/auth/oidc/choose`,
    authPending: `${P}/auth/pending`,
    /** SMS owner check of a client account with several users: GET state, POST resend / verify. */
    ownerCheck: `${P}/auth/owner-check`,
    ownerCheckResend: `${P}/auth/owner-check/resend`,
    ownerCheckVerify: `${P}/auth/owner-check/verify`,
    /** GET: what activation copies from the WHMCS client; POST: activate. */
    join: `${P}/auth/join`,
    logout: `${P}/auth/logout`,
    /** «برگشت به پنل مدیریت»: ends an admin's read-only view of this panel. */
    impersonateStop: `${P}/auth/impersonate/stop`,
    me: `${P}/me`,
    onboarding: `${P}/onboarding`,
    onboardingShare: `${P}/onboarding/share`,
    /** Public: level ladder and rates for the program page (no sign-in, open while the panel is closed). */
    program: `${P}/program`,

    // inbox
    notifications: `${P}/notifications`,
    notificationRead: (id: string) => `${P}/notifications/${encodeURIComponent(id)}/read`,
    notificationsReadAll: `${P}/notifications/read-all`,

    // account
    profile: `${P}/profile`,
    profileNationalCode: `${P}/profile/national-code`,
    notificationPrefs: `${P}/notification-prefs`,
    notifyMobile: `${P}/notify-mobile`,
    notifyMobileRequest: `${P}/notify-mobile/request`,
    notifyMobileVerify: `${P}/notify-mobile/verify`,
    ambassador: `${P}/ambassador`,
    special: `${P}/special`,
    specialCheck: `${P}/special/check`,
    specialOne: (id: number) => `${P}/special/${id}`,
    specialWithdraw: (id: number) => `${P}/special/${id}/withdraw`,
    /** «کمپین فروش»: GET view, POST start (multipart: `data` JSON + optional `logo`). */
    campaign: `${P}/special/campaign`,
    campaignCancel: (id: number) => `${P}/special/campaign/${id}/cancel`,
    /** «پورسانت بر اساس گردش مالی»: GET view, POST join. */
    turnover: `${P}/special/turnover`,
    turnoverLeave: `${P}/special/turnover/leave`,

    // money
    balance: `${P}/balance`,
    payouts: `${P}/payouts`,
    payoutReceipt: (id: string) => `${P}/payouts/${encodeURIComponent(id)}/receipt`,
    payoutRule: `${P}/payout-rule`,
    bankAccounts: `${P}/bank-accounts`,

    // sharing
    promoCodes: `${P}/promo-codes`,
    promoExpire: (id: number) => `${P}/promo-codes/${id}/expire`,
    promoBatch: `${P}/promo-codes/batch`,
    promoPack: `${P}/promo-codes/pack`,
    promoPackCodes: `${P}/promo-codes/pack/codes`,
    pluginSites: `${P}/plugin/sites`,
    pluginSiteCodes: `${P}/plugin/site-codes`,
    pluginSite: (id: number) => `${P}/plugin/sites/${id}`,
    links: `${P}/links`,
    link: (id: number) => `${P}/links/${id}`,
    linkStats: (days: number, link: string) => `${P}/links/stats?days=${days}&link=${encodeURIComponent(link)}`,
    linkQr: `${P}/links/qr`,
    refCode: `${P}/ref-code`,
    refCodeCheck: (code: string) => `${P}/ref-code/check?code=${encodeURIComponent(code)}`,

    // portal pages
    home: `${P}/home`,
    commissions: `${P}/commissions`,
    legacyCommissions: `${P}/legacy-commissions`,
    legacyCommissionsExport: `${P}/legacy-commissions/export`,
    legacyPayouts: `${P}/legacy-payouts`,
    services: `${P}/services`,
    claims: `${P}/claims`,
    earnings: `${P}/earnings`,
    levels: `${P}/levels`,
    /** «تغییر قرارداد»: GET what a contract partner would get on the ladder; POST `{agree: true}` switches (no way back from the panel). */
    contractSwitch: `${P}/contract-switch`,
    kit: `${P}/kit`,
    rules: `${P}/rules`,
    rulesAccept: `${P}/rules/accept`,

    // panel content
    /** Home slider: active slides in order. */
    slides: `${P}/slides`,
    /** «پیشنهاد / گزارش مشکل»: GET the partner's own (newest first), POST a new one. */
    feedback: `${P}/feedback`,
} as const;
